import React, { useState, useEffect } from 'react';
import { Card, Select, Button, Space, Tag, Spin, message, Tooltip, Badge } from 'antd';
import { UserOutlined, TeamOutlined, SaveOutlined, CloseOutlined, StarFilled } from '@ant-design/icons';
import axios from 'axios';
import { getTokens } from '../../services/authApi';
import './TechAssignmentEditor.css';

const { Option } = Select;

interface TechAssignmentEditorProps {
  appointmentId: number;
  currentPrimaryTechId: number;
  currentAdditionalTechIds?: number[];
  onSave: () => void;
  onCancel?: () => void;
  compact?: boolean;
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  role: string;
}

export const TechAssignmentEditor: React.FC<TechAssignmentEditorProps> = ({
  appointmentId,
  currentPrimaryTechId,
  currentAdditionalTechIds = [],
  onSave,
  onCancel,
  compact = false
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [technicians, setTechnicians] = useState<User[]>([]);
  const [primaryTechId, setPrimaryTechId] = useState<number>(currentPrimaryTechId);
  const [additionalTechIds, setAdditionalTechIds] = useState<number[]>(currentAdditionalTechIds);

  useEffect(() => {
    loadTechnicians();
  }, []);

  const loadTechnicians = async () => {
    setLoading(true);
    try {
      const tokens = getTokens();
      const headers = tokens?.accessToken ? {
        Authorization: `Bearer ${tokens.accessToken}`
      } : {};

      const [techniciansResponse, ownersResponse] = await Promise.all([
        axios.get('/api/users', {
          params: { role: 'technician', status: 'active' },
          headers
        }),
        axios.get('/api/users', {
          params: { role: 'owner', status: 'active' },
          headers
        })
      ]);

      const allUsers = [...techniciansResponse.data, ...ownersResponse.data].sort((a, b) => {
        const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
        const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setTechnicians(allUsers);
    } catch (error) {
      console.error('Error loading technicians:', error);
      message.error('Failed to load technicians');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const allTechIds = [primaryTechId, ...additionalTechIds];

      await axios.put(`/api/appointments/${appointmentId}/technicians`, {
        technician_ids: allTechIds,
        primary_technician_id: primaryTechId
      });

      message.success('Technician assignments updated');
      onSave();
    } catch (error) {
      console.error('Error updating technicians:', error);
      message.error('Failed to update technician assignments');
    } finally {
      setSaving(false);
    }
  };

  const handlePrimaryChange = (techId: number) => {
    // If the new primary was in additional list, remove it
    if (additionalTechIds.includes(techId)) {
      setAdditionalTechIds(additionalTechIds.filter(id => id !== techId));
    }

    // If the old primary wasn't in additional list and isn't being removed entirely, add it
    if (primaryTechId && !additionalTechIds.includes(primaryTechId)) {
      // User will need to manually add old primary to additional if they want to keep it
    }

    setPrimaryTechId(techId);
  };

  const getTechName = (techId: number): string => {
    const tech = technicians.find(t => t.id === techId);
    return tech ? `${tech.first_name} ${tech.last_name}` : 'Unknown';
  };

  const getTechRole = (techId: number): string => {
    const tech = technicians.find(t => t.id === techId);
    return tech?.role || 'technician';
  };

  const hasChanges = () => {
    if (primaryTechId !== currentPrimaryTechId) return true;
    if (additionalTechIds.length !== currentAdditionalTechIds.length) return true;
    return !additionalTechIds.every(id => currentAdditionalTechIds.includes(id));
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Spin size="small" />
      </div>
    );
  }

  if (compact) {
    return (
      <div className="tech-assignment-editor-compact">
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          <div className="tech-assignment-field">
            <label>Primary Tech:</label>
            <Select
              value={primaryTechId}
              onChange={handlePrimaryChange}
              style={{ width: '100%' }}
              size="small"
              showSearch
              filterOption={(input, option) =>
                (option?.label as string).toLowerCase().includes(input.toLowerCase())
              }
              options={technicians.map(tech => ({
                value: tech.id,
                label: `${tech.first_name} ${tech.last_name}`
              }))}
            />
          </div>

          <div className="tech-assignment-field">
            <label>Additional Techs:</label>
            <Select
              mode="multiple"
              value={additionalTechIds}
              onChange={setAdditionalTechIds}
              style={{ width: '100%' }}
              size="small"
              placeholder="None"
              showSearch
              filterOption={(input, option) =>
                (option?.label as string).toLowerCase().includes(input.toLowerCase())
              }
              options={technicians
                .filter(tech => tech.id !== primaryTechId)
                .map(tech => ({
                  value: tech.id,
                  label: `${tech.first_name} ${tech.last_name}`
                }))}
            />
          </div>

          {hasChanges() && (
            <Space size="small">
              <Button
                type="primary"
                size="small"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saving}
              >
                Save
              </Button>
              <Button
                size="small"
                icon={<CloseOutlined />}
                onClick={onCancel}
              >
                Cancel
              </Button>
            </Space>
          )}
        </Space>
      </div>
    );
  }

  return (
    <Card className="tech-assignment-editor" size="small">
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <div className="assignment-section-title">
            <UserOutlined /> Primary Technician
          </div>
          <Select
            value={primaryTechId}
            onChange={handlePrimaryChange}
            style={{ width: '100%' }}
            showSearch
            filterOption={(input, option) =>
              (option?.label as string).toLowerCase().includes(input.toLowerCase())
            }
          >
            {technicians.map(tech => (
              <Option key={tech.id} value={tech.id}>
                <Space>
                  {tech.first_name} {tech.last_name}
                  <Tag color={tech.role === 'owner' ? 'purple' : 'blue'}>
                    {tech.role}
                  </Tag>
                </Space>
              </Option>
            ))}
          </Select>
        </div>

        <div>
          <div className="assignment-section-title">
            <TeamOutlined /> Additional Technicians
          </div>
          <Select
            mode="multiple"
            value={additionalTechIds}
            onChange={setAdditionalTechIds}
            style={{ width: '100%' }}
            placeholder="Select additional technicians"
            showSearch
            filterOption={(input, option) =>
              (option?.label as string).toLowerCase().includes(input.toLowerCase())
            }
            maxTagCount="responsive"
          >
            {technicians
              .filter(tech => tech.id !== primaryTechId)
              .map(tech => (
                <Option key={tech.id} value={tech.id}>
                  <Space>
                    {tech.first_name} {tech.last_name}
                    <Tag color={tech.role === 'owner' ? 'purple' : 'blue'}>
                      {tech.role}
                    </Tag>
                  </Space>
                </Option>
              ))}
          </Select>
        </div>

        {/* Current Assignment Preview */}
        <div className="current-assignment-preview">
          <div className="preview-label">Current Assignment:</div>
          <Space wrap>
            <Tooltip title="Primary Technician">
              <Badge.Ribbon text="Primary" color="blue">
                <Tag icon={<StarFilled />} color="blue" className="tech-preview-tag">
                  {getTechName(primaryTechId)}
                </Tag>
              </Badge.Ribbon>
            </Tooltip>
            {additionalTechIds.map(techId => (
              <Tag key={techId} color="default" className="tech-preview-tag">
                {getTechName(techId)}
              </Tag>
            ))}
          </Space>
        </div>

        {/* Action Buttons */}
        {hasChanges() && (
          <div className="assignment-actions">
            <Space>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saving}
              >
                Save Changes
              </Button>
              <Button
                icon={<CloseOutlined />}
                onClick={onCancel}
              >
                Cancel
              </Button>
            </Space>
          </div>
        )}

        {!hasChanges() && (
          <div className="no-changes-message">
            <Tag color="success">No changes to save</Tag>
          </div>
        )}
      </Space>
    </Card>
  );
};
